import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { TableCells } from 'models';

import { formatTableData } from 'utils';
import _get from 'lodash/get';

import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';

import clsx from 'clsx';
import { useStyles } from './style';

export interface Props<T extends Record<string, any>> {
  item: T;
  tableCells: TableCells<T>[];
  handleRowClick?: (item: T) => void;
}

function TableRow<T extends {}>({
  item,
  tableCells,
  handleRowClick,
}: React.PropsWithChildren<Props<T>>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down(theme.size.xs));
  const small = useMediaQuery(
    theme.breakpoints.between(theme.size.xs, theme.size.sm)
  );
  const cssStyles = useMemo(
    () => ({ cursor: handleRowClick ? 'pointer' : 'default' }),
    [handleRowClick]
  );
  const classes = useStyles(cssStyles);

  const mainCells = useMemo(() => {
    return tableCells.filter((cell) => {
      return small ? _get(cell, 'small') : xs ? _get(cell, 'xs') : true;
    });
  }, [tableCells, small, xs]);

  const otherCells = useMemo(() => {
    return tableCells.filter((cell) => {
      return small ? !_get(cell, 'small') : xs ? !_get(cell, 'xs') : false;
    });
  }, [tableCells, small, xs]);

  return (
    <>
      <MuiTableRow classes={{ root: classes.row }}>
        {mainCells.map((cell) => {
          return (
            <MuiTableCell
              key={cell.id}
              style={cell.rowStyle}
              classes={{ root: classes.cell }}
              onClick={handleRowClick ? () => handleRowClick(item) : undefined}
            >
              {cell.render ? (
                cell.render(item, cell.id)
              ) : (
                <span className={classes.overflow}>
                  {formatTableData(item, cell)}
                </span>
              )}
            </MuiTableCell>
          );
        })}
      </MuiTableRow>
      {otherCells.map((cell, idx) => {
        const isLast = otherCells.length - 1 === idx;

        return (
          <MuiTableRow classes={{ root: classes.row }}>
            <MuiTableCell
              colSpan={mainCells.length}
              key={cell.id}
              // style={cell.rowStyle}
              classes={{
                root: clsx(classes.cell, {
                  [classes.last]: isLast,
                }),
              }}
              onClick={handleRowClick ? () => handleRowClick(item) : undefined}
            >
              {
                <>
                  {t(cell.label)}
                  {':'}{' '}
                  {cell.render
                    ? cell.render(item, cell.id)
                    : formatTableData(item, cell)}
                </>
              }
            </MuiTableCell>
          </MuiTableRow>
        );
      })}
    </>
  );
}

export default memo(TableRow);
